import clsx from 'clsx';
import { useState, useRef, useEffect } from 'react';
import type { DragEvent, ChangeEvent } from 'react';

import { Icon } from '@flintmint/web-shared/components/icon';
import { Input } from '@flintmint/web-shared/components/input';
import { Modal } from '@flintmint/web-shared/components/modal';
import { Loader } from '@flintmint/web-shared/components/loader';
import { Button } from '@flintmint/web-shared/components/button';
import { Collapse } from '@flintmint/web-shared/components/collapse';
import { useNavigateWithSearch } from '@/hooks/use-navigate-with-search';

import { useRouteInfo } from '@/hooks/use-route-info';
import { useItemActions } from '@/hooks/use-item-actions';
import { useUserSourceCreate } from '@/hooks/rxdb-models';
import { getAuthUser } from '@flintmint/web-shared/hooks/use-auth-user';

import { uploadFile } from '@/services/firebase-storage-service';

import { appRoutes } from '@/constants/routes';
import { isValidUrl } from '@/utils/helper-utils';

import type { UserSource } from '@flintmint/core-shared/rxdb';

import styles from './add-user-source-modal.module.scss';

const VALID_TYPES = ['text/plain', 'text/markdown', 'application/pdf'];
const VALID_EXTENSIONS = ['.txt', '.md', '.pdf'];
const MAX_MB = 10;
const MAX_SIZE = MAX_MB * 1024 * 1024;

const uploadErrorMessages = {
  size: `The selected file is too large.\n Maximum allowed size is ${MAX_MB}MB.`,
  type: `The selected file type is not supported.\n Only ${VALID_EXTENSIONS.join(' and ')} files are allowed.`,
  default: `There was a problem while uploading a file.\n Please delete and try again.`,
};

export const AddUserSourceModal = (props: AddUserSourceModalProps) => {
  const { open, onClose } = props;

  const { id: userId } = getAuthUser();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [storageFileName, setStorageFileName] = useState<string | null>(null);
  const [documentUrl, setDocumentUrl] = useState('');

  const { isCortex } = useRouteInfo();
  const { createNew } = useItemActions();
  const { mutate: createUserSource } = useUserSourceCreate();

  const navigateWithSearch = useNavigateWithSearch();

  const getContentType = (file: File): string => {
    if (file.type) {
      return file.type;
    }

    // Some files sometimes don't have a MIME type
    const fileExtension = file.name.toLowerCase().split('.').pop();
    if (fileExtension === 'md') {
      return 'text/markdown';
    }
    if (fileExtension === 'txt') {
      return 'text/plain';
    }
    if (fileExtension === 'pdf') {
      return 'application/pdf';
    }
    return '';
  };

  const validateFile = (file: File) => {
    const contentType = getContentType(file);
    const isValidType = VALID_TYPES.includes(contentType);

    setSelectedFile(file);

    if (!isValidType) {
      setError(uploadErrorMessages.type);
      return false;
    }

    if (file.size > MAX_SIZE) {
      setError(uploadErrorMessages.size);
      return false;
    }

    return true;
  };

  const onModalClose = () => {
    onClose?.();
    setError(null);
    setSelectedFile(null);
    setStorageFileName(null);
    setUploadProgress(0);
    setIsUploading(false);
  };

  // handles drag and drop and file input change events
  const onDrop = (e: DragEvent<HTMLDivElement> | ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setIsDragActive(false);

    if (isUploading) {
      return;
    }

    const file = 'dataTransfer' in e ? e.dataTransfer?.files[0] : e.target?.files?.[0];

    if (!file || !validateFile(file)) {
      return;
    }

    // If file has no content type, create a new File with the correct type
    const correctedFile = file.type ? file : new File([file], file.name, { type: getContentType(file) });
    setSelectedFile(correctedFile);
    setStorageFileName(`${crypto.randomUUID()}-${correctedFile.name}`);
    setUploadProgress(0);
    setError(null);
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      // only happens if the drag leaves the drop zone (not just drags over a child element)
      setIsDragActive(false);
    }
  };

  const onFileUploadError = () => {
    setError(uploadErrorMessages.default);
    setUploadProgress(100);
    setIsUploading(false);
  };

  const getFileNameWithoutExtension = (fileName: string) => {
    const parts = fileName.toLowerCase().split('.');
    parts.pop();
    return parts.join('.');
  };

  const onFileUploadSuccess = (uploadedFile: File, uploadedStorageFileName: string) => {
    return createUserSource(
      {
        name: getFileNameWithoutExtension(uploadedFile.name),
        sourceType: getContentType(uploadedFile),
        source: `users/${userId}/${uploadedStorageFileName}`,
        sourceDisplayName: uploadedFile.name,
        syncStatus: 'processing',
      },
      {
        onSuccess: (createdSource: UserSource) => {
          onModalClose();
          isCortex && navigateWithSearch(`${appRoutes.cortex}/${createdSource.id}`);
        },
      }
    );
  };

  const handleUpload = (uploadedFile: File, uploadedStorageFileName: string) => {
    if (!userId) {
      return;
    }
    setIsUploading(true);
    uploadFile(
      userId!,
      uploadedFile,
      (progress) => setUploadProgress(progress),
      () => onFileUploadSuccess(uploadedFile, uploadedStorageFileName),
      onFileUploadError,
      uploadedStorageFileName
    );
  };

  const handleAreaClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  const addUrlUserSource = () => {
    return createUserSource(
      {
        name: documentUrl,
        sourceType: 'url',
        source: documentUrl,
        syncStatus: 'processing',
      },
      {
        onSuccess: (createdSource: UserSource) => {
          onModalClose();
          setDocumentUrl('');
          navigateWithSearch(`${appRoutes.cortex}/${createdSource.id}`);
        },
      }
    );
  };

  useEffect(() => {
    if (!selectedFile || !storageFileName || error) return;
    handleUpload(selectedFile, storageFileName);
  }, [selectedFile, storageFileName, error]);

  return (
    <Modal
      open={open}
      topDivider={false}
      onClose={onModalClose}
      title="Add Cortex Item"
      closeOnClickOutside={false}
      className={clsx(styles.modalContent, {
        [styles.uploadInProgress]: isUploading,
      })}
      data-testid="add-user-source-modal"
      secondaryButton={{
        onClick: onModalClose,
        appearance: 'clear',
      }}
      primaryButton={{
        onClick: addUrlUserSource,
        children: 'Add Item',
        loading: isUploading,
        disabled: !isValidUrl(documentUrl),
      }}
    >
      <div
        onDrop={onDrop}
        onClick={handleAreaClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={(e) => e.preventDefault()}
        className={clsx(styles.dropZone, {
          [styles.dragActive]: isDragActive,
        })}
        role="region"
        aria-label="File upload drop zone. Click or drag and drop files here"
      >
        <input type="file" onChange={onDrop} ref={fileInputRef} data-testid="file-input" accept={VALID_EXTENSIONS.join(',')} />
        <div className={styles.messageHolder}>
          <Icon type="cloud-upload-bulk" multiColor className={styles.cloudUploadIcon} size={48} />
          <div className={styles.message}>
            <p>{isDragActive ? 'Drop Here!' : 'Drag & Drop any document here'}</p>
            <p className={styles.secondaryMessage}>or start a new file from scratch</p>
          </div>
          <Button
            color="grey"
            onClick={(e) => {
              e.stopPropagation();
              createNew({ type: 'userSources' });
              onModalClose();
            }}
          >
            New Blank Item
          </Button>
        </div>
      </div>
      <Input
        type="url"
        size="big"
        gap="medium"
        value={documentUrl}
        appearance="filled"
        className={styles.urlInput}
        placeholder="Enter document URL"
        left={<Icon type="browser" size={24} />}
        onChange={(e) => setDocumentUrl(e.target.value)}
      />
      {selectedFile && (
        <div
          className={clsx(styles.uploadProgress, {
            [styles.error]: !!error,
            [styles.inProgress]: isUploading,
          })}
        >
          <Icon type="file-01" size={20} />
          <div className={styles.progress}>
            <span className="ellipsis-text">{selectedFile.name}</span>
            <Loader type="progress" error={!!error} progress={uploadProgress} data-testid="progress-bar" />
            <Collapse open={!!error}>
              <span className={styles.error}>{error}</span>
            </Collapse>
          </div>
          {!!error && <Icon type="delete-01" data-testid="delete-btn" size={20} onClick={() => setSelectedFile(null)} />}
        </div>
      )}
    </Modal>
  );
};

interface AddUserSourceModalProps {
  open: boolean;
  onClose: () => void;
}
